import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Portal,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { PencilSimple, Plus, Trash } from "@phosphor-icons/react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { H2 } from "../../../../components/text/headings";
import { ResponsiveLine } from "@nivo/line";
import {
  useGetMultiYearEmissions,
  useGetQuarterlyMultiYearEmissions,
} from "../../../../hooks/query/emissions-insight/emissions-insights-query";
import {
  useGetEmissionsTargets,
  useCreateEmissionsTarget,
  useUpdateEmissionsTarget,
  useDeleteEmissionsTarget,
  useGetMultiYearCompanyTotalEmissions,
  useGetQuarterlyMultiYearCompanyTotalEmissions,
} from "../../../../hooks/query/reports";
import useAirUserRoles from "../../../../utils/AuthUtils";
import { getFormattedNumValue } from "../../../../utils";
import { YearStartEndDropdown } from "../supplier-components/SupplierTrends";
import LocalSpinner from "../../../../components/LocalSpinner";
import WaterfallChart from "./WaterfallChart";
import ConsumptionVsEmissionsChart from "./ConsumptionVsEmissionsChart";
import QuartersSwitch from "./details/QuartersSwitch";
import { axisBaselineLayer } from "./Consumption/chartTheme";

import { SCOPE_COLORS } from "./KpiCharts";

const SCOPE_SERIES = [
  { key: "scope1Total", label: "Scope 1", color: SCOPE_COLORS.scope1.base, lightColor: SCOPE_COLORS.scope1.light, textColor: SCOPE_COLORS.scope1.text },
  { key: "scope2LocationTotal", label: "Scope 2 (Location)", color: SCOPE_COLORS.scope2.base, lightColor: SCOPE_COLORS.scope2.light, textColor: SCOPE_COLORS.scope2.text },
  { key: "scope2MarketTotal", label: "Scope 2 (Market)", color: SCOPE_COLORS.scope2.base, lightColor: SCOPE_COLORS.scope2.light, textColor: SCOPE_COLORS.scope2.text },
  { key: "scope3Total", label: "Scope 3", color: SCOPE_COLORS.scope3.base, lightColor: SCOPE_COLORS.scope3.light, textColor: SCOPE_COLORS.scope3.text },
  { key: "total", label: "Total (S1+S2+S3)", color: "#333333", lightColor: "#E2E8F0", textColor: "#1A202C" },
];

// Org-wide reduction targets, persisted server-side (ESG-3934) via
// /api/insights/emissions-reduction-targets. Each target maps `scopes` (one or
// more series keys) to a single reduction trajectory from its base year to
// base + duration; the baseline and trajectory are resolved client-side from
// the chart data.

const TARGET_PALETTE = ["#E11D48", "#0EA5E9", "#F59E0B", "#10B981", "#7C3AED", "#EA580C"];
const colorForTargetIndex = (i) => TARGET_PALETTE[i % TARGET_PALETTE.length];

// Available scopes a target can apply to. `total` is excluded — a target
// against the rolled-up total wouldn't combine sensibly with the per-scope
// filtering in the chart.
const TARGET_SCOPE_OPTIONS = SCOPE_SERIES.filter((s) => s.key !== "total");

const TargetEditorModal = ({ isOpen, onClose, onSave, initial, defaultYear }) => {
  const [name, setName] = useState(initial?.name ?? "");
  const [baseYear, setBaseYear] = useState(String(initial?.baseYear ?? defaultYear ?? new Date().getFullYear()));
  const [durationYears, setDurationYears] = useState(String(initial?.durationYears ?? 5));
  const [reductionPct, setReductionPct] = useState(String(initial?.reductionPct ?? 30));
  const [scopes, setScopes] = useState(new Set(initial?.scopes ?? ["scope1Total", "scope2LocationTotal", "scope3Total"]));

  // Re-prime when the modal reopens for a different target.
  useEffect(() => {
    if (!isOpen) return;
    setName(initial?.name ?? "");
    setBaseYear(String(initial?.baseYear ?? defaultYear ?? new Date().getFullYear()));
    setDurationYears(String(initial?.durationYears ?? 5));
    setReductionPct(String(initial?.reductionPct ?? 30));
    setScopes(new Set(initial?.scopes ?? ["scope1Total", "scope2LocationTotal", "scope3Total"]));
  }, [isOpen, initial, defaultYear]);

  const toggleScope = (key) => {
    setScopes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const canSave =
    name.trim().length > 0
    && Number.isFinite(Number(baseYear))
    && Number(durationYears) > 0
    && Number(reductionPct) >= 0
    && Number(reductionPct) <= 100
    && scopes.size > 0;

  const handleSave = () => {
    if (!canSave) return;
    // No client id — the server assigns it on create; on edit `initial.id` is kept.
    onSave({
      ...(initial?.id ? { id: initial.id } : {}),
      name: name.trim(),
      baseYear: Number(baseYear),
      durationYears: Number(durationYears),
      reductionPct: Number(reductionPct),
      scopes: [...scopes],
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg">{initial ? "Edit target" : "Add target"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <FormControl>
              <FormLabel fontSize="sm">Name</FormLabel>
              <Input
                size="sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. SBTi 1.5°C target"
              />
            </FormControl>
            <Flex gap={3}>
              <FormControl>
                <FormLabel fontSize="sm">Base year</FormLabel>
                <Input
                  size="sm"
                  type="number"
                  value={baseYear}
                  onChange={(e) => setBaseYear(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Duration (years)</FormLabel>
                <Input
                  size="sm"
                  type="number"
                  min={1}
                  value={durationYears}
                  onChange={(e) => setDurationYears(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Reduction %</FormLabel>
                <Input
                  size="sm"
                  type="number"
                  min={0}
                  max={100}
                  value={reductionPct}
                  onChange={(e) => setReductionPct(e.target.value)}
                />
              </FormControl>
            </Flex>
            <FormControl>
              <FormLabel fontSize="sm">Scopes</FormLabel>
              <VStack align="stretch" spacing={1}>
                {TARGET_SCOPE_OPTIONS.map((s) => (
                  <Checkbox
                    key={s.key}
                    isChecked={scopes.has(s.key)}
                    onChange={() => toggleScope(s.key)}
                    colorScheme="limeShade"
                  >
                    <Text fontSize="sm">{s.label}</Text>
                  </Checkbox>
                ))}
              </VStack>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            bg="darkTeal"
            color="white"
            isDisabled={!canSave}
            onClick={handleSave}
            _hover={{ opacity: 0.9 }}
          >
            {initial ? "Save changes" : "Add target"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const TargetsList = ({ targets, onEdit, onDelete, onAdd, canEdit = true }) => (
  <Flex direction="column" gap={2} mt={2} mb={3}>
    <Flex align="center" justify="space-between">
      <Text fontSize="13px" fontWeight="600" color="gray.700">
        Targets {targets.length > 0 && (<Text as="span" color="gray.400" fontWeight="400">({targets.length})</Text>)}
      </Text>
      {canEdit && (
        <Button
          size="xs"
          variant="outline"
          borderColor="limeShade.600"
          color="limeShade.600"
          leftIcon={<Plus size={12} />}
          onClick={onAdd}
          _hover={{ bg: "lightLime" }}
        >
          Add target
        </Button>
      )}
    </Flex>
    {targets.length === 0 && (
      <Text fontSize="12px" color="gray.400">
        {canEdit
          ? "No targets yet. Add one to overlay a reduction line on the chart."
          : "No targets yet."}
      </Text>
    )}
    {targets.map((t, i) => (
      <Flex
        key={t.id}
        align="center"
        gap={2}
        px={3}
        py={2}
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
      >
        <Box w="14px" h="3px" bg={colorForTargetIndex(i)} borderRadius="full" />
        <Box flex={1} minW={0}>
          <Text fontSize="13px" fontWeight="600" color="gray.700" noOfLines={1}>
            {t.name}
          </Text>
          <Text fontSize="11px" color="gray.500" noOfLines={1}>
            {t.reductionPct}% reduction by {t.baseYear + t.durationYears} from {t.baseYear} ·{" "}
            {t.scopes.map((k) => SCOPE_SERIES.find((s) => s.key === k)?.label || k).join(", ")}
          </Text>
        </Box>
        {canEdit && (
          <>
            <IconButton
              aria-label="Edit target"
              icon={<PencilSimple size={14} />}
              size="xs"
              variant="ghost"
              color="gray.500"
              onClick={() => onEdit(t)}
            />
            <IconButton
              aria-label="Delete target"
              icon={<Trash size={14} />}
              size="xs"
              variant="ghost"
              color="gray.500"
              _hover={{ color: "red.500" }}
              onClick={() => onDelete(t)}
            />
          </>
        )}
      </Flex>
    ))}
  </Flex>
);

// Portal-anchored tooltip. Nivo's default tooltip renders inside the chart
// wrapper, which gets clipped by the Accordion panel's overflow. We instead
// portal it to document.body, anchor it at the mouse cursor, and clamp its
// position so it can't fall outside the viewport. The position is read from
// a window-level mousemove listener because Nivo doesn't pass cursor coords
// into the tooltip render fn.
const EdgeAwareTooltip = ({ children }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  // Track the latest cursor position globally; Nivo's tooltip mounts at
  // hover-time so the listener will already have a value by then.
  useEffect(() => {
    const onMove = (e) => {
      setPos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  // Clamp the tooltip into the viewport once it's measured.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !pos) return;
    const rect = el.getBoundingClientRect();
    const margin = 8;
    const offsetX = 12; // small gap from the cursor
    const offsetY = 12;
    let left = pos.x + offsetX;
    let top = pos.y + offsetY;
    if (left + rect.width > window.innerWidth - margin) {
      left = pos.x - rect.width - offsetX;
    }
    if (left < margin) left = margin;
    if (top + rect.height > window.innerHeight - margin) {
      top = pos.y - rect.height - offsetY;
    }
    if (top < margin) top = margin;
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  }, [pos, children]);

  if (!pos) return null;
  return (
    <Portal>
      <Box
        ref={ref}
        bg="white"
        p="8px"
        border="1px solid #ccc"
        borderRadius="4px"
        boxShadow="md"
        style={{
          position: "fixed",
          left: pos.x + 12,
          top: pos.y + 12,
          zIndex: 1500,
          pointerEvents: "none",
        }}
      >
        {children}
      </Box>
    </Portal>
  );
};

const EmissionsTrends = ({ dimensionParams, selectedYear, quarter }) => {
  const toast = useToast();

  const currentYear = new Date().getFullYear();
  const [startYear, setStartYear] = useState(currentYear - 4);
  const [endYear, setEndYear] = useState(currentYear);
  const [visibleScopes, setVisibleScopes] = useState(
    () => new Set(SCOPE_SERIES.map((s) => s.key))
  );
  // Quarterly mode: plot one point per quarter (Q1 2024, Q2 2024, …) instead
  // of one per year. Ignores the page's quarter filter — all four quarters
  // are always plotted.
  const [showQuarters, setShowQuarters] = useState(false);

  // Org-wide reduction targets, persisted server-side (ESG-3934). Each entry
  // shapes as { id, name, baseYear, durationYears, scopes: string[],
  // reductionPct }. Only org admins can add/edit/delete; everyone else sees
  // them read-only (matches the backend authority split).
  const { isOrgAdmin } = useAirUserRoles();
  const { data: targets = [] } = useGetEmissionsTargets();

  const onTargetError = () =>
    toast({ title: "Could not save target", status: "error", duration: 4000, isClosable: true });
  const createTarget = useCreateEmissionsTarget({ onError: onTargetError });
  const updateTarget = useUpdateEmissionsTarget({ onError: onTargetError });
  const removeTarget = useDeleteEmissionsTarget({ onError: onTargetError });

  const targetEditor = useDisclosure();
  const [editingTarget, setEditingTarget] = useState(null);
  const openAddTarget = () => { setEditingTarget(null); targetEditor.onOpen(); };
  const openEditTarget = (t) => { setEditingTarget(t); targetEditor.onOpen(); };
  const saveTarget = (t) => {
    if (t.id) updateTarget.mutate(t);
    else createTarget.mutate(t);
  };
  const deleteTarget = (t) => removeTarget.mutate(t.id);

  const years = useMemo(
    () => Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i),
    [startYear, endYear]
  );

  const queries = useGetMultiYearEmissions(
    years,
    dimensionParams,
    {
      onError: (error) => {
        // A 404 just means that year in the probed span has no report — the
        // chart skips it and plots the years that do. Don't toast on it.
        if (error?.response?.status === 404) return;
        toast({
          title: "Error fetching emissions trend data",
          description: error?.response?.data?.parameters?.message || error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
      retry: false,
      cacheTime: 60,
    },
    quarter
  );

  // Quarterly series: one query per (year, quarter). Only fetched while the
  // quarterly switch is on. The page-level quarter filter is deliberately
  // NOT passed — all four quarters are plotted regardless.
  const quarterlyQueries = useGetQuarterlyMultiYearEmissions(years, dimensionParams, {
    enabled: showQuarters,
    onError: (error) => {
      // Quarters without a report 404 — the chart just skips them.
      if (error?.response?.status === 404) return;
      toast({
        title: "Error fetching quarterly emissions trend data",
        description: error?.response?.data?.parameters?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
    retry: false,
    cacheTime: 60,
  });

  // Scope 3 for this chart comes from the PERSISTED report
  // (/reports/total-emissions) — the same source as the Scope 3 summary card
  // and the Scope 3 drill-down — NOT from /insights/total. The insights
  // endpoint recomputes Scope 3 live from questionnaire rows on every call,
  // and that recompute has been observed to diverge wildly from the finalised
  // report for some years (e.g. 17.7M vs 249k tCO2e for 2023). Reading the
  // persisted figure keeps the chart consistent with everything else on the
  // page. Note: like the Scope 3 card, this source is org-wide — the
  // dimension filter does not apply to Scope 3 here.
  const scope3Queries = useGetMultiYearCompanyTotalEmissions(years, quarter || "ALL", {
    retry: false,
    cacheTime: 60_000,
  });
  const quarterlyScope3Queries = useGetQuarterlyMultiYearCompanyTotalEmissions(years, {
    enabled: showQuarters,
    retry: false,
    cacheTime: 60_000,
  });

  const isFetching =
    queries.some((q) => q.isFetching) ||
    scope3Queries.some((q) => q.isFetching) ||
    (showQuarters &&
      (quarterlyQueries.some((q) => q.isFetching) || quarterlyScope3Queries.some((q) => q.isFetching)));

  const dataByYear = useMemo(() => {
    const out = {};
    queries.forEach((q, i) => {
      if (q.data) {
        const d = q.data;
        const scope3 = Number(scope3Queries[i]?.data?.scopeThreeTotalCo2Emissions) || 0;
        out[years[i]] = {
          scope1Total: d.scope1Total || 0,
          scope2LocationTotal: d.scope2LocationTotal || 0,
          scope2MarketTotal: d.scope2MarketTotal || 0,
          scope3Total: scope3,
          total: (d.scope1AndScope2LocationTotal || 0) + scope3,
        };
      }
    });
    return out;
  }, [queries, scope3Queries, years]);

  // Quarterly payloads keyed by "Q1 2024"-style labels, in chronological
  // order (the flatMap in the hooks is year-major, quarter-minor — both
  // arrays share the same indexing).
  const dataByQuarter = useMemo(() => {
    const out = {};
    quarterlyQueries.forEach((q, i) => {
      if (!q.data) return;
      const year = years[Math.floor(i / 4)];
      const quarterLabel = `Q${(i % 4) + 1} ${year}`;
      const d = q.data;
      const scope3 = Number(quarterlyScope3Queries[i]?.data?.scopeThreeTotalCo2Emissions) || 0;
      out[quarterLabel] = {
        scope1Total: d.scope1Total || 0,
        scope2LocationTotal: d.scope2LocationTotal || 0,
        scope2MarketTotal: d.scope2MarketTotal || 0,
        scope3Total: scope3,
        total: (d.scope1AndScope2LocationTotal || 0) + scope3,
      };
    });
    return out;
  }, [quarterlyQueries, quarterlyScope3Queries, years]);

  // X-axis domain in display order: years, or every quarter of every year.
  const periodLabels = useMemo(() => {
    if (!showQuarters) return years.map(String);
    return years.flatMap((y) => ["Q1", "Q2", "Q3", "Q4"].map((q) => `${q} ${y}`));
  }, [showQuarters, years]);

  const dataByPeriod = showQuarters ? dataByQuarter : dataByYear;

  // Period labels that actually have data — this is the x-axis domain Nivo's
  // point scale will build from the actual series. Target series must only
  // sample at these x values: introducing a label with no actuals (e.g. a
  // quarter of a year reported annually) appends it to the END of the point
  // scale's domain, scrambling the axis order and making the target line
  // double back across the chart.
  const plottedPeriods = useMemo(
    () => periodLabels.filter((p) => dataByPeriod[p] !== undefined),
    [periodLabels, dataByPeriod]
  );

  // Quarterly mode shows ONLY values recorded against a quarter. Data entered
  // at the yearly level belongs to no quarter and is deliberately not spread
  // or plotted — this flag (any year whose annual total exceeds the sum of
  // its plotted quarters) drives an explanatory note under the chart.
  const hasYearlyOnlyEntries = useMemo(() => {
    if (!showQuarters) return false;
    return years.some((y) => {
      const annual = dataByYear[y];
      if (!annual) return false;
      const present = ["Q1", "Q2", "Q3", "Q4"]
        .map((q) => `${q} ${y}`)
        .filter((l) => dataByQuarter[l]);
      if (present.length === 0) return false;
      return SCOPE_SERIES.some(({ key }) => {
        const qSum = present.reduce((acc, l) => acc + (dataByQuarter[l][key] || 0), 0);
        return (annual[key] || 0) - qSum > 0.005;
      });
    });
  }, [showQuarters, years, dataByYear, dataByQuarter]);

  // Years shown on the annual line but absent from the quarterly view — data
  // for them exists only at the yearly level, so no quarter request returns
  // anything. Listed in a hint so the gap doesn't read as missing data.
  const yearsWithoutQuarterlyData = useMemo(() => {
    if (!showQuarters) return [];
    return years.filter(
      (y) =>
        dataByYear[y] !== undefined &&
        !["Q1", "Q2", "Q3", "Q4"].some((q) => dataByQuarter[`${q} ${y}`] !== undefined)
    );
  }, [showQuarters, years, dataByYear, dataByQuarter]);

  const baseChartData = useMemo(() => {
    return SCOPE_SERIES
      .filter((series) => visibleScopes.has(series.key))
      .map((series) => ({
        id: series.label,
        color: series.color,
        data: periodLabels
          .filter((p) => dataByPeriod[p] !== undefined)
          .map((p) => ({
            x: p,
            y: dataByPeriod[p][series.key],
          })),
      }))
      .filter((series) => series.data.length > 0);
  }, [dataByPeriod, periodLabels, visibleScopes]);

  // Fractional year position of a period label on the x-axis: "2024" → 2024,
  // "Q3 2024" → 2024.5. Lets the annual target trajectory be sampled at any
  // period granularity — quarterly points sit a quarter-year apart on the
  // same straight line.
  const periodToFraction = useCallback(
    (label) => {
      if (!showQuarters) return Number(label);
      const [q, y] = label.split(" ");
      return Number(y) + (Number(q[1]) - 1) / 4;
    },
    [showQuarters]
  );

  // Targets are defined against ANNUAL emissions, but each quarterly actual
  // is only ~a quarter of a year's footprint. To make the comparison honest
  // in quarterly mode, each sampled target value is divided by 4 — the
  // quarterly share of the annual budget at that point on the trajectory.
  const targetPeriodScale = showQuarters ? 4 : 1;

  // Build target overlay series. A target is visible only if at least one
  // of its scopes is currently shown. Its baseline is the sum of those
  // visible scopes' actual values for the base year. The target line runs
  // from (baseYear, baseline) to (baseYear + duration, baseline × (1 -
  // reductionPct/100)) and is sampled at every period label in the chart's
  // domain (so Nivo's point scale, which only knows about x values actually
  // in the data, has a known x for each plotted point). In quarterly mode
  // the same annual trajectory is linearly interpolated at each quarter's
  // fractional year position. Points outside the [startYear, endYear]
  // window are clamped — the line still slopes correctly even if the
  // target horizon extends beyond the chart range.
  const targetSeries = useMemo(() => {
    return targets
      .map((t, i) => {
        const visibleTargetScopes = t.scopes.filter((k) => visibleScopes.has(k));
        if (visibleTargetScopes.length === 0) return null;
        const baseYearData = dataByYear[t.baseYear];
        if (!baseYearData) return null;
        const baseValue = visibleTargetScopes.reduce((acc, k) => acc + (baseYearData[k] || 0), 0);
        if (baseValue <= 0) return null;
        const endValue = baseValue * (1 - t.reductionPct / 100);
        const targetEndYear = t.baseYear + t.durationYears;
        const slope = (endValue - baseValue) / (targetEndYear - t.baseYear);

        // Sample one point per PLOTTED period inside the target window so
        // every x lands on a label already in the point scale's domain
        // (see plottedPeriods above).
        const points = plottedPeriods
          .filter((p) => {
            const frac = periodToFraction(p);
            return frac >= t.baseYear && frac <= targetEndYear;
          })
          .map((p) => ({
            x: p,
            y: (baseValue + slope * (periodToFraction(p) - t.baseYear)) / targetPeriodScale,
          }));
        if (points.length < 2) return null;
        return {
          id: `${t.name} (target)`,
          color: colorForTargetIndex(i),
          dashed: true,
          data: points,
        };
      })
      .filter(Boolean);
  }, [targets, visibleScopes, dataByYear, plottedPeriods, periodToFraction, targetPeriodScale]);

  const chartData = useMemo(() => [...baseChartData, ...targetSeries], [baseChartData, targetSeries]);

  // Lowest plotted value — used as the area fill's baseline. Nivo fills areas
  // down to areaBaselineValue (default 0); with a y-scale starting at "auto"
  // (the data minimum) a zero baseline lies far below the plot area, so the
  // fill bleeds over the axis labels and legend. Baselining at the data
  // minimum clips the shading at the effective x-axis line.
  const yMin = useMemo(() => {
    let min = Infinity;
    chartData.forEach((s) => s.data.forEach((d) => {
      const v = Number(d.y);
      if (Number.isFinite(v)) min = Math.min(min, v);
    }));
    return Number.isFinite(min) ? min : 0;
  }, [chartData]);

  // Y-axis unit scales with what's actually shown: a Scope 2-only view in the
  // tens of thousands reads in ktCO2e (or tCO2e) rather than "0.1, 0.1, 0.1"
  // MtCO2e gradations.
  const yAxisScale = useMemo(() => {
    const maxY = chartData.reduce(
      (acc, s) => s.data.reduce((m, d) => Math.max(m, Number(d.y) || 0), acc),
      0
    );
    if (maxY >= 10_000_000) return { divisor: 1_000_000, unit: "MtCO2e" };
    if (maxY >= 10_000) return { divisor: 1_000, unit: "ktCO2e" };
    return { divisor: 1, unit: "tCO2e" };
  }, [chartData]);

  // Look up applicable targets for an actual data point: which target series
  // cover the same scope-set as the hovered point, and what the target value
  // is at that point's year. Used by the tooltip to show how far above/below
  // the target trajectory the actual sits.
  //
  // We match a target to an actual series only if the target's scopes
  // exactly equal the actual scope's key — i.e. a Scope 1 target lights up
  // the Scope 1 series tooltip, a multi-scope target (e.g. S1+S2) lights up
  // the Total series tooltip. Avoids confusing the user by showing them a
  // S1+S2 target when they're hovering Scope 3.
  const targetLookup = useMemo(() => {
    // Map: actualSeriesLabel -> [{ name, color, valueByYear: {year: number} }]
    const out = {};
    targets.forEach((t, i) => {
      const visibleTargetScopes = t.scopes.filter((k) => visibleScopes.has(k));
      if (visibleTargetScopes.length === 0) return;
      const baseYearData = dataByYear[t.baseYear];
      if (!baseYearData) return;
      const baseValue = visibleTargetScopes.reduce((acc, k) => acc + (baseYearData[k] || 0), 0);
      if (baseValue <= 0) return;
      const endValue = baseValue * (1 - t.reductionPct / 100);
      const targetEndYear = t.baseYear + t.durationYears;
      const slope = (endValue - baseValue) / (targetEndYear - t.baseYear);
      // Keyed by period label ("2024" or "Q3 2024") so the tooltip lookup
      // works in both annual and quarterly mode.
      const valueByYear = {};
      plottedPeriods
        .filter((p) => {
          const frac = periodToFraction(p);
          return frac >= t.baseYear && frac <= targetEndYear;
        })
        .forEach((p) => {
          valueByYear[p] = (baseValue + slope * (periodToFraction(p) - t.baseYear)) / targetPeriodScale;
        });

      // Pick which actual series labels this target should annotate. If the
      // target covers exactly one scope, attach to that scope's label. If
      // it covers more than one, attach to the Total label.
      const targetLabels = visibleTargetScopes.length === 1
        ? [SCOPE_SERIES.find((s) => s.key === visibleTargetScopes[0])?.label].filter(Boolean)
        : ["Total (S1+S2+S3)"];

      targetLabels.forEach((label) => {
        if (!out[label]) out[label] = [];
        out[label].push({
          name: t.name,
          color: colorForTargetIndex(i),
          valueByYear,
        });
      });
    });
    return out;
  }, [targets, visibleScopes, dataByYear, plottedPeriods, periodToFraction, targetPeriodScale]);

  const toggleScope = (key) => {
    setVisibleScopes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const hasData = chartData.length > 0 && chartData.some((s) => s.data.length > 0);

  // When the accordion is expanded we scroll the section heading to the top
  // of the layout's scroll container (the app uses a fixed-height shell with
  // an internal scroll area, NOT the document body — `window.scrollTo` and
  // `el.scrollIntoView` against the window don't move anything). We
  // calculate the target position relative to that container and drive it
  // directly.
  const sectionRef = useRef(null);
  const handleAccordionChange = (expandedIndices) => {
    const isOpen = Array.isArray(expandedIndices)
      ? expandedIndices.length > 0
      : expandedIndices >= 0;
    if (!isOpen || !sectionRef.current) return;

    // Find the actual scroll container by walking up the ancestor chain
    // looking for a y-scrollable element.
    const el = sectionRef.current;
    const findScroller = () => {
      let node = el.parentElement;
      while (node && node !== document.documentElement) {
        const cs = window.getComputedStyle(node);
        const oy = cs.overflowY;
        const scrollable = oy === "auto" || oy === "scroll" || oy === "overlay";
        if (scrollable && node.scrollHeight - node.clientHeight > 1) return node;
        node = node.parentElement;
      }
      return document.getElementById("layoutContainer");
    };
    const scroller = findScroller();
    if (!scroller) return;

    // Chakra's accordion focuses the AccordionButton on click, which forces
    // the browser to scrollIntoView. That fires AFTER React's onChange and
    // overrides any scroll we apply in the same frame. We can't observe its
    // completion timing reliably across browsers, so we run a manual smooth
    // animation that clamps scrollTop on every frame for ~350ms — outlasting
    // any single focus-driven scroll restore.
    const elRect0 = el.getBoundingClientRect();
    const scRect0 = scroller.getBoundingClientRect();
    const start = scroller.scrollTop;
    const target = Math.max(0, start + (elRect0.top - scRect0.top) - 8);
    if (Math.abs(target - start) < 1) return;

    const duration = 350;
    const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic

    const step = (now) => {
      const elapsed = now - t0;
      const t = Math.min(1, elapsed / duration);
      const value = start + (target - start) * ease(t);
      scroller.scrollTop = value;
      if (t < 1) {
        requestAnimationFrame(step);
      }
    };

    // Defer the first frame so it runs after Chakra's focus side-effects.
    setTimeout(() => requestAnimationFrame(step), 0);
  };

  return (
    <Box ref={sectionRef}>
      <Accordion allowToggle onChange={handleAccordionChange}>
        <AccordionItem borderTop="none" borderBottom="1px solid" borderColor="gray.200">
          <AccordionButton>
            <H2 fontSize="body.2xl">Emissions Trends</H2>
            <AccordionIcon mr={0} ml="auto" />
          </AccordionButton>

          <AccordionPanel mt={4}>
            <Tabs variant="primary" isLazy>
              <TabList>
                <Tab>Time</Tab>
                <Tab>Change</Tab>
                <Tab>Consumption v Emissions</Tab>
              </TabList>
              <TabPanels>
                <TabPanel px={0}>
                  <Flex direction="column" gap={3}>
                    <Flex gap={2} flexWrap="wrap" justify="space-between" align="center">
                      <HStack spacing={4}>
                        <YearStartEndDropdown
                          setStartYear={setStartYear}
                          startYear={startYear}
                          setEndYear={setEndYear}
                          endYear={endYear}
                        />
                        <QuartersSwitch isChecked={showQuarters} onChange={setShowQuarters} />
                      </HStack>
                      <HStack spacing={2} flexWrap="wrap">
                        {SCOPE_SERIES.map((s) => {
                          const active = visibleScopes.has(s.key);
                          return (
                            <Tag
                              key={s.key}
                              size="sm"
                              borderRadius="full"
                              cursor="pointer"
                              border="1px solid"
                              borderColor={active ? s.color : "gray.300"}
                              bg={active ? s.lightColor : "transparent"}
                              color={active ? s.textColor : "gray.500"}
                              fontWeight="600"
                              onClick={() => !active && toggleScope(s.key)}
                              _hover={{ bg: active ? s.lightColor : "gray.50" }}
                              transition="all 0.15s"
                            >
                              <TagLabel>{s.label}</TagLabel>
                              {active && (
                                <TagCloseButton
                                  onClick={(e) => { e.stopPropagation(); toggleScope(s.key); }}
                                />
                              )}
                            </Tag>
                          );
                        })}
                      </HStack>
                    </Flex>

                    <Box>
                      <Text fontSize="body.m" fontWeight="600" color="gray.700">
                        Emissions over time
                      </Text>
                      <Text fontSize="body.xs" color="gray.500">
                        {showQuarters
                          ? "Quarterly "
                          : quarter && quarter !== "ALL"
                            ? `${quarter} `
                            : "Annual "}
                        tCO2e by scope, {startYear} → {endYear}. Use the tags to show or
                        hide each scope.
                      </Text>
                    </Box>

                    {isFetching && <LocalSpinner />}

                    <TargetsList
                      targets={targets}
                      canEdit={isOrgAdmin}
                      onAdd={openAddTarget}
                      onEdit={openEditTarget}
                      onDelete={deleteTarget}
                    />

                    <Box height="360px" width="100%">
                      {hasData && !isFetching && (
                        <ResponsiveLine
                          data={chartData}
                          margin={{ top: 30, right: 30, bottom: showQuarters ? 80 : 60, left: 100 }}
                          xScale={{ type: "point" }}
                          yScale={{ type: "linear", min: "auto", max: "auto", stacked: false }}
                          curve="monotoneX"
                          enableArea={true}
                          areaOpacity={0.12}
                          areaBaselineValue={yMin}
                          theme={{
                            axis: {
                              ticks: {
                                text: { fontSize: 12, fill: "#718096" },
                              },
                              legend: {
                                text: { fontSize: 13, fill: "#2D3748", fontWeight: 600 },
                              },
                            },
                            legends: {
                              text: { fontSize: 12, fontWeight: 600, fill: "#4A5568" },
                            },
                          }}
                          axisBottom={{
                            legend: showQuarters ? "Quarter" : "Year",
                            legendOffset: showQuarters ? 52 : 42,
                            legendPosition: "middle",
                            tickPadding: 8,
                            // Up to 20 quarterly labels — rotate so they don't collide.
                            tickRotation: showQuarters ? -45 : 0,
                          }}
                          axisLeft={{
                            legend: `Emissions (${yAxisScale.unit})`,
                            legendOffset: -80,
                            legendPosition: "middle",
                            tickPadding: 6,
                            format: (v) =>
                              (v / yAxisScale.divisor).toLocaleString("en-GB", {
                                maximumFractionDigits: 1,
                              }),
                          }}
                          enableGridX={false}
                          enablePoints={false}
                          colors={(d) => d.color}
                          lineWidth={2}
                          enableTouchCrosshair={true}
                          useMesh={true}
                          // Custom Lines + Points layers. Nivo's built-in
                          // `pointBorderColor: { from: "color" }` inheritor
                          // doesn't always resolve to the series colour in
                          // this version, so we render points ourselves —
                          // white fill, stroke matching the series colour.
                          // The Lines layer also handles dashed target
                          // trajectories vs solid actuals.
                          layers={[
                            "grid",
                            "markers",
                            "axes",
                            axisBaselineLayer,
                            "areas",
                            "crosshair",
                            ({ series, lineGenerator, xScale, yScale }) => (
                              <g>
                                {series.map((s) => {
                                  const isTarget = s.id.endsWith("(target)");
                                  const path = lineGenerator(
                                    s.data.map((d) => ({
                                      x: xScale(d.data.x),
                                      y: yScale(d.data.y),
                                    }))
                                  );
                                  return (
                                    <path
                                      key={s.id}
                                      d={path}
                                      fill="none"
                                      stroke={s.color}
                                      strokeWidth={2}
                                      strokeDasharray={isTarget ? "6 4" : undefined}
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  );
                                })}
                              </g>
                            ),
                            ({ series, xScale, yScale }) => (
                              <g>
                                {series.map((s) =>
                                  s.data.map((d, i) => (
                                    <circle
                                      key={`${s.id}-${i}`}
                                      cx={xScale(d.data.x)}
                                      cy={yScale(d.data.y)}
                                      r={3}
                                      fill="#FFFFFF"
                                      stroke={s.color}
                                      strokeWidth={2}
                                    />
                                  ))
                                )}
                              </g>
                            ),
                            "slices",
                            "mesh",
                          ]}
                          tooltip={({ point }) => {
                            const isTargetSeries = point.serieId.endsWith("(target)");
                            const matchedTargets = isTargetSeries
                              ? []
                              : (targetLookup[point.serieId] || []);
                            const actualValue = Number(point.data.y);
                            return (
                              <EdgeAwareTooltip>
                                <Text fontWeight="bold" color={point.color} fontSize="sm">
                                  {point.serieId}
                                </Text>
                                <Text fontSize="sm">
                                  {showQuarters ? "Quarter" : "Year"}: {point.data.x}
                                </Text>
                                <Text fontSize="sm">
                                  {getFormattedNumValue(actualValue, 2)} tCO2e
                                </Text>
                                {matchedTargets.map((t) => {
                                  const targetVal = t.valueByYear[point.data.x];
                                  if (targetVal == null) return null;
                                  const diff = actualValue - targetVal;
                                  const pct = targetVal !== 0
                                    ? (diff / targetVal) * 100
                                    : null;
                                  // Above target = bad (over budget). Use the
                                  // same colour cues as the YoY chips.
                                  const overTarget = diff > 0;
                                  const arrow = overTarget ? "↑" : "↓";
                                  const colorTone = overTarget ? "red.600" : "green.600";
                                  return (
                                    <Box key={t.name} mt={1.5} pt={1.5} borderTop="1px solid" borderColor="gray.100">
                                      <Text fontSize="xs" color={t.color} fontWeight="700">
                                        {t.name}
                                      </Text>
                                      <Text fontSize="xs" color="gray.600">
                                        Target: {getFormattedNumValue(targetVal, 2)} tCO2e
                                        {showQuarters ? " (quarterly share)" : ""}
                                      </Text>
                                      {pct != null && (
                                        <Text fontSize="xs" color={colorTone} fontWeight="600">
                                          {arrow} {Math.abs(pct).toFixed(1)}% {overTarget ? "above" : "below"} target
                                        </Text>
                                      )}
                                    </Box>
                                  );
                                })}
                              </EdgeAwareTooltip>
                            );
                          }}
                        />
                      )}
                      {!hasData && !isFetching && (
                        <Flex align="center" justify="center" h="100%" color="gray.400">
                          <Text>No emissions data available for the selected range.</Text>
                        </Flex>
                      )}
                    </Box>
                    {hasData && !isFetching && (
                      <Flex justify="center" wrap="wrap" gap={5} mt={-4} mb={2}>
                        {chartData.map((s) => (
                          <Flex key={s.id} align="center" gap={2}>
                            <Box
                              as="svg"
                              width="14px"
                              height="14px"
                              viewBox="0 0 14 14"
                              flexShrink={0}
                            >
                              <circle cx="7" cy="7" r="5" fill="#FFFFFF" stroke={s.color} strokeWidth="2" />
                            </Box>
                            <Text fontSize="12px" fontWeight="600" color="#4A5568">
                              {s.id}
                            </Text>
                          </Flex>
                        ))}
                      </Flex>
                    )}
                    {hasYearlyOnlyEntries && !isFetching && (
                      <Text fontSize="body.xs" color="gray.500">
                        Some emissions were recorded as yearly entries and are not attributed to a
                        specific quarter — they are not included in the quarterly view, so quarter
                        values may read lower than the annual view.
                      </Text>
                    )}
                    {yearsWithoutQuarterlyData.length > 0 && !isFetching && (
                      <Text fontSize="body.xs" color="gray.500">
                        {yearsWithoutQuarterlyData.join(", ")}{" "}
                        {yearsWithoutQuarterlyData.length === 1 ? "has" : "have"} no quarterly
                        data — emissions for {yearsWithoutQuarterlyData.length === 1 ? "that year" : "those years"}{" "}
                        were reported at the yearly level only, so they appear on the annual view
                        but not here.
                      </Text>
                    )}
                    <Text fontSize="body.xs" color="gray.500">
                      A rising line is a rising tCO2e footprint. Compare scope lines to see which
                      pillar is driving change — Scope 3 typically dominates for most companies.
                    </Text>
                  </Flex>
                </TabPanel>
                <TabPanel px={0}>
                  <WaterfallChart dimensionParams={dimensionParams} selectedYear={selectedYear} quarter={quarter} />
                </TabPanel>
                <TabPanel px={0}>
                  <ConsumptionVsEmissionsChart
                    dimensionParams={dimensionParams}
                    selectedYear={selectedYear}
                    quarter={quarter}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
            {/* Target editor modal lives outside the Tabs so it can overlay
                cleanly regardless of which sub-tab is open. */}
            <TargetEditorModal
              isOpen={targetEditor.isOpen}
              onClose={targetEditor.onClose}
              onSave={saveTarget}
              initial={editingTarget}
              defaultYear={endYear}
            />
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  );
};

export default EmissionsTrends;
